import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { PanelModel } from '@syncfusion/ej2-layouts';
import { DashboardClient, DashboardDTO, DashboardPanelDTO, UpdateDashboardPanelsDTO } from '../../../base/models/models.swagger.generated';
import { WidgetType } from './widget-type.enum';

export interface ExtendedPanelModel extends PanelModel {
  widgetType: string;
  widgetParameters: string;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  public panels: ExtendedPanelModel[];

  constructor(private dashboardClient: DashboardClient) { }

  resolve(): Observable<DashboardDTO> {
    return this.dashboardClient.getDashboard().pipe(
      mergeMap((dashboard: DashboardDTO) => {
        if (dashboard) {
          this.updatePanels(dashboard);
          return of(dashboard);
        } else {
          // by default we want the user to have a logo widget
          return this.updateDashboardPanels(new UpdateDashboardPanelsDTO({addedPanels: [new DashboardPanelDTO({
            id: 0,
            sizeX: 3,
            sizeY: 2,
            row: 0,
            col: 1,
            widgetType: WidgetType[WidgetType.LogoWidget]
          })]}));
        }
      })
    );
  }

  public updateDashboardPanels(updateDashboardPanelsData: UpdateDashboardPanelsDTO): Observable<DashboardDTO> {
    return this.dashboardClient.updateDashboardPanels(updateDashboardPanelsData).pipe(
      tap((dashboard: DashboardDTO) => {
        this.updatePanels(dashboard);
      })
    );
  }

  public doesPanelCollide(panelToCheck: ExtendedPanelModel): boolean {
    let collisionFound = false;
    this.panels?.forEach(panel => {
      collisionFound = collisionFound || this.isCoveredByPanel(panel.col, panel.row, panelToCheck);
      collisionFound = collisionFound || this.isCoveredByPanel(panel.col + panel.sizeX - 1, panel.row, panelToCheck);
      collisionFound = collisionFound || this.isCoveredByPanel(panel.col, panel.row + panel.sizeY - 1, panelToCheck);
      collisionFound = collisionFound || this.isCoveredByPanel(panel.col + panel.sizeX - 1, panel.row + panel.sizeY - 1, panelToCheck);
    });
    return collisionFound;
  }

  private isCoveredByPanel(col: number, row: number, panel: ExtendedPanelModel): boolean {
    if (col >= panel.col && col < panel.col + panel.sizeX && row >= panel.row && row < panel.row + panel.sizeY) {
      return true;
    }
    return false;
  }

  private updatePanels(dashboard: DashboardDTO) {
    if (!dashboard) {
      return null;
    }
    this.panels = [];
    dashboard.dashboardPanels?.forEach((panel) => {
      this.panels.push({
        id: panel.id.toString(),
        sizeX: panel.sizeX,
        sizeY: panel.sizeY,
        row: panel.row,
        col: panel.col,
        widgetType: panel.widgetType,
        widgetParameters: panel.widgetParameters
      } as ExtendedPanelModel);
    });
  }
}
